/**
 * ExecutiveMemo — the only report. One scroll, no tabs, no charts.
 *
 * Reads exactly like the Monday-morning email a brand rep sends to
 * their CMO. Sections in priority order:
 *
 *   1. THE BOTTOM LINE   — spend, revenue, ROAS, verdict, repeat?
 *   2. WHAT WORKED        — bullet rows with attributed $$ values
 *   3. WHAT DIDN'T        — bullet rows with drag/lost $$ values
 *   4. RECOMMENDED NEXT WAVE — 3-5 plain English sentences
 *   5. small-print context — creators · levers · duration · impressions
 *
 * Backend already turns the math into sentence-ready strings; this
 * component is mostly typography and layout — minimal logic on the
 * client.
 */
import React from 'react';
import {
  ArrowUpRight, ArrowDownRight, ThumbsUp, ThumbsDown, Minus,
  Diamond, Users, Zap, AlertTriangle,
} from 'lucide-react';

// ─── Verdict colors (kept inline so this component is self-contained) ──
const VERDICT_COLOR = {
  big_win:   { fg: '#4ade80', bg: 'rgba(74,222,128,0.10)', border: 'rgba(74,222,128,0.30)' },
  on_target: { fg: '#00D4FF', bg: 'rgba(0,212,255,0.10)',  border: 'rgba(0,212,255,0.30)' },
  shortfall: { fg: '#fbbf24', bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.30)' },
  miss:      { fg: '#FF6B6B', bg: 'rgba(255,107,107,0.10)', border: 'rgba(255,107,107,0.30)' },
  no_signal: { fg: '#A78BFA', bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.30)' },
  no_target: { fg: '#fbbf24', bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.30)' },
};

const ICON_FOR_ROW = {
  team:        Users,
  bridge:      Diamond,
  lever:       Zap,
  thumbs_up:   ThumbsUp,
  thumbs_down: ThumbsDown,
  neutral:     Minus,
};

// ─── One bullet row inside What Worked / What Didn't ───────────────────
function MemoRow({ row, polarity }) {
  const Icon = ICON_FOR_ROW[row.icon] || Users;
  const isNeg = polarity === 'negative';
  const valueColor = isNeg ? '#FF6B6B' : '#4ade80';
  return (
    <div
      data-testid={`memo-row-${row.icon}`}
      className="flex items-start gap-3 py-3"
      style={{ borderTop: '1px solid #1a1f2b' }}
    >
      <span
        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
        style={{
          background: isNeg ? 'rgba(255,107,107,0.10)' : 'rgba(74,222,128,0.10)',
          border:     `1px solid ${isNeg ? 'rgba(255,107,107,0.30)' : 'rgba(74,222,128,0.30)'}`,
        }}
      >
        <Icon className="h-3.5 w-3.5" style={{ color: valueColor }} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] text-white leading-snug font-bold">{row.sentence}</p>
        {row.subtitle && (
          <p className="text-[11.5px] text-gray-500 leading-snug mt-0.5">{row.subtitle}</p>
        )}
      </div>
      <span
        className="flex-shrink-0 text-[16px] font-black tabular-nums tracking-[-0.02em]"
        style={{ color: valueColor }}
        data-testid={`memo-row-${row.icon}-value`}
      >
        {row.value}
      </span>
    </div>
  );
}

// ─── Next-wave recommendation row ──────────────────────────────────────
function NextWaveRow({ rec }) {
  const Icon = ICON_FOR_ROW[rec.icon] || Minus;
  const color = rec.icon === 'thumbs_up' ? '#4ade80'
              : rec.icon === 'thumbs_down' ? '#FF6B6B'
              : '#A78BFA';
  return (
    <li
      data-testid={`memo-nextwave-${rec.icon}`}
      className="flex items-start gap-3 py-2.5"
    >
      <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color }} />
      <p className="text-[14px] text-white leading-snug">{rec.sentence}</p>
    </li>
  );
}

export default function ExecutiveMemo({ data, isSnapshot, snapshotLabel }) {
  if (!data) return null;
  const h = data.headline || {};
  const verdict = h.verdict || {};
  const verdictColor = VERDICT_COLOR[verdict.key] || VERDICT_COLOR.on_target;
  const ctx = data.context || {};

  return (
    <div data-testid="executive-memo" className="flex flex-col gap-7">
      {isSnapshot && (
        <div
          className="rounded-md px-3 py-2 text-[11px]"
          style={{ background: 'rgba(167,139,250,0.10)', color: '#A78BFA', border: '1px solid rgba(167,139,250,0.30)' }}
          data-testid="memo-snapshot-banner"
        >
          Viewing saved snapshot · <b>{snapshotLabel}</b> · refresh to return to live data
        </div>
      )}

      {/* ─── 1. THE BOTTOM LINE ───────────────────────────────────── */}
      <section data-testid="memo-bottom-line">
        <p className="text-[10.5px] uppercase tracking-[0.20em] font-black" style={{ color: '#A78BFA' }}>
          The Bottom Line
          {h.primary_kpi?.label && (
            <span className="ml-2 text-gray-500">· primary KPI: {h.primary_kpi.label}{h.primary_kpi.category && ` (${h.primary_kpi.category})`}</span>
          )}
        </p>
        <h2
          className="text-[34px] sm:text-[42px] font-black tracking-[-0.03em] text-white mt-3 leading-[1.05]"
          data-testid="memo-headline-line"
        >
          {/* KPI-aware headline.
              Performance KPI (roas, gmv, conversions, ctr) -> "$spent -> $revenue -> 4.2× ROAS"
              Branding KPI (reach, engagement_rate, brand_lift, ...) -> "$spent -> 320K reach (vs 250K target)" */}
          {(h.primary_kpi?.category === 'performance' && h.revenue) ? (
            <>
              {h.spent} spent
              <span className="text-gray-600 mx-2">→</span>
              {h.revenue} revenue
              {h.primary_kpi?.current_fmt && h.primary_kpi.current > 0 && (
                <>
                  <span className="text-gray-600 mx-2">→</span>
                  <span style={{ color: verdictColor.fg }}>{h.primary_kpi.current_fmt} {h.primary_kpi.label}</span>
                </>
              )}
            </>
          ) : h.primary_kpi?.label ? (
            <>
              {h.spent !== '—' && (
                <>
                  {h.spent} spent
                  <span className="text-gray-600 mx-2">→</span>
                </>
              )}
              <span style={{ color: verdictColor.fg }}>
                {h.primary_kpi.current_fmt} {h.primary_kpi.label}
              </span>
              {h.primary_kpi.target_fmt && (
                <span className="text-gray-500 text-[24px] sm:text-[28px] ml-3 font-bold">
                  vs {h.primary_kpi.target_fmt} target
                </span>
              )}
            </>
          ) : (
            // No primary KPI declared — fallback to spend only.
            <>{h.spent} spent</>
          )}
        </h2>
        <div
          className="inline-flex items-center gap-3 mt-5 px-4 py-3 rounded-lg"
          style={{ background: verdictColor.bg, border: `1px solid ${verdictColor.border}` }}
          data-testid="memo-verdict"
        >
          {verdict.recommend_repeat
            ? <ArrowUpRight className="h-5 w-5 flex-shrink-0" style={{ color: verdictColor.fg }} />
            : <ArrowDownRight className="h-5 w-5 flex-shrink-0" style={{ color: verdictColor.fg }} />}
          <div>
            <p className="text-[11.5px] uppercase tracking-[0.18em] font-black" style={{ color: verdictColor.fg }}>
              {verdict.label}
            </p>
            <p className="text-[15px] font-bold text-white mt-0.5">{verdict.headline}</p>
          </div>
        </div>
        {h.is_estimated && (
          <p className="text-[10.5px] text-gray-500 italic mt-3" data-testid="memo-estimated-flag">
            Revenue estimated from engagement (no Lens A conversion pixel wired yet).
          </p>
        )}
      </section>

      {/* ─── 2. WHAT WORKED ──────────────────────────────────────── */}
      {data.what_worked?.length > 0 && (
        <section data-testid="memo-what-worked">
          <p className="text-[10.5px] uppercase tracking-[0.20em] font-black" style={{ color: '#4ade80' }}>
            What Worked
          </p>
          <div className="mt-3">
            {data.what_worked.map((row, i) => (
              <MemoRow key={`${row.icon || 'r'}-${i}-${(row.sentence || '').slice(0, 24)}`} row={row} polarity="positive" />
            ))}
          </div>
        </section>
      )}

      {/* ─── 3. WHAT DIDN'T ──────────────────────────────────────── */}
      {data.what_didnt?.length > 0 && (
        <section data-testid="memo-what-didnt">
          <p className="text-[10.5px] uppercase tracking-[0.20em] font-black" style={{ color: '#FF6B6B' }}>
            What Didn't
          </p>
          <div className="mt-3">
            {data.what_didnt.map((row, i) => (
              <MemoRow key={`${row.icon || 'r'}-${i}-${(row.sentence || '').slice(0, 24)}`} row={row} polarity="negative" />
            ))}
          </div>
        </section>
      )}

      {/* ─── 4. RECOMMENDED NEXT WAVE ────────────────────────────── */}
      <section data-testid="memo-next-wave">
        <p className="text-[10.5px] uppercase tracking-[0.20em] font-black" style={{ color: '#A78BFA' }}>
          Recommended Next Wave
        </p>
        <ul className="mt-2">
          {(data.next_wave || []).map((rec, i) => (
            <NextWaveRow key={`${rec.icon || 'n'}-${i}-${(rec.sentence || '').slice(0, 24)}`} rec={rec} />
          ))}
        </ul>
      </section>

      {/* ─── 5. context (small print) ────────────────────────────── */}
      <p className="text-[10.5px] text-gray-500 leading-relaxed" data-testid="memo-context">
        {[
          ctx.creators    ? `${ctx.creators} creators`                                 : null,
          ctx.lever_fires ? `${ctx.lever_fires} lever${ctx.lever_fires === 1 ? '' : 's'} fired` : null,
          ctx.duration    ? `${ctx.duration} duration`                                 : null,
          ctx.total_reach ? `${ctx.total_reach.toLocaleString()} impressions`          : null,
        ].filter(Boolean).join(' · ')}
      </p>
    </div>
  );
}
